import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/store"
import {
  createWorkflow,
  updateWorkflow,
  fetchWorkflows,
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  selectWorkflows,
  selectTasksForSelected,
  setSelectedWorkflow,
} from "../../store/workflowSlice"
import {
  UpsertWorkflowDto,
  UpsertWorkflowTaskDto,
  WorkflowTaskDto,
} from "../../dtos/workflow.dto"
import { toast } from "sonner"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { ArrowLeft, Edit, Trash2, MoveUp, MoveDown } from "lucide-react"
import { WorkflowConfirmationDialog } from "../components/WorkflowConfirmationDialog"
import { TooltipWrap } from "@/modules/shared/ui/components/tooltip-wrap"

const workflowFormSchema = z.object({
  name: z.string().min(1, "Workflow name is required"),
  description: z.string().optional(),
})

type WorkflowFormValues = z.infer<typeof workflowFormSchema>

const taskFormSchema = z.object({
  title: z
    .string()
    .min(1, "Task title is required")
    .regex(
      /^[a-zA-Z0-9\s\-_]+$/,
      "Title can only contain letters, numbers, spaces, hyphens, and underscores",
    ),
  defaultAssignedToEmail: z
    .string()
    .min(1, "Assignee email is required")
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Please enter a valid email address"),
  description: z.string().min(1, "Task description is required"),
  dueAtOffsetDays: z.number().int().min(0).optional(),
})

type TaskFormValues = z.infer<typeof taskFormSchema>
type DraftTask = UpsertWorkflowTaskDto & { id: string }
type TaskLike = WorkflowTaskDto | DraftTask

export default function WorkflowBuilderPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch<AppDispatch>()

  const workflows = useSelector(selectWorkflows)
  const tasks = useSelector(selectTasksForSelected)
  const { isLoading } = useSelector((state: RootState) => state.workflows)

  const workflowForm = useForm<WorkflowFormValues>({
    resolver: zodResolver(workflowFormSchema),
    defaultValues: { name: "", description: "" },
    mode: "onBlur",
  })

  const taskForm = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      defaultAssignedToEmail: "",
      description: "",
      dueAtOffsetDays: undefined,
    },
    mode: "onBlur",
  })

  const [taskMode, setTaskMode] = useState<"create" | "edit">("create")
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [draftTasks, setDraftTasks] = useState<DraftTask[]>([])
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    type: "unsaved" | "delete-task"
    taskId?: string
    taskTitle?: string
  }>({
    open: false,
    type: "unsaved",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const formInitialized = useRef(false)

  const isEditing = Boolean(id)
  const currentWorkflow = workflows.find((w) => w.id === id)
  const isExisting = Boolean(currentWorkflow)
  const displayedTasks: TaskLike[] = isExisting ? tasks : draftTasks

  useEffect(() => {
    const subscription = workflowForm.watch(() => {
      setHasUnsavedChanges(true)
    })
    return () => subscription.unsubscribe()
  }, [workflowForm])

  useEffect(() => {
    if (isEditing && id && !formInitialized.current) {
      const workflow = workflows.find((w) => w.id === id)
      if (workflow) {
        workflowForm.reset({
          name: workflow.name,
          description: workflow.description || "",
        })
        dispatch(setSelectedWorkflow(workflow))
        dispatch(fetchTasks(workflow.id))
        formInitialized.current = true
      } else {
        dispatch(fetchWorkflows())
          .unwrap()
          .then((loaded) => {
            const found = loaded.find((w) => w.id === id)
            if (found) {
              workflowForm.reset({
                name: found.name,
                description: found.description || "",
              })
              dispatch(setSelectedWorkflow(found))
              dispatch(fetchTasks(found.id))
              formInitialized.current = true
            }
          })
      }
    } else if (!isEditing && !formInitialized.current) {
      setDraftTasks([])
      formInitialized.current = true
    }
  }, [dispatch, id, isEditing, workflows, workflowForm])

  const handleSaveWorkflow = async (values: WorkflowFormValues) => {
    const taskPayloads = displayedTasks
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((task: TaskLike) => {
        const {
          id,
          title,
          description,
          defaultAssignedToEmail = "",
          orderIndex,
          dueAtOffsetDays,
        } = task
        void id
        const email = defaultAssignedToEmail.trim()
        return {
          title: title.trim(),
          description: description?.trim() || "",
          defaultAssignedToEmail: email,
          orderIndex,
          dueAtOffsetDays: dueAtOffsetDays ?? null,
        }
      })

    const cleanedPayload: UpsertWorkflowDto = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      isActive: true,
      tasks: taskPayloads.length ? taskPayloads : undefined,
    }

    try {
      if (isEditing && id && currentWorkflow) {
        await dispatch(updateWorkflow({ id, payload: cleanedPayload })).unwrap()
        toast.success("Workflow updated")
      } else {
        await dispatch(createWorkflow(cleanedPayload)).unwrap()
        setDraftTasks([])
        toast.success("Workflow created")
        navigate("/onboarding/workflows", { replace: true })
      }
      setHasUnsavedChanges(false)
    } catch (err) {
      toast.error((err as string) || "Failed to save workflow")
    }
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        type: "unsaved",
      })
      return
    }
    navigate("/onboarding/workflows")
  }

  const handleConfirmUnsaved = () => {
    setConfirmDialog({ open: false, type: "unsaved" })
    navigate("/onboarding/workflows")
  }

  const openTaskCreate = () => {
    setTaskMode("create")
    setEditingTaskId(null)
    taskForm.reset({
      title: "",
      defaultAssignedToEmail: "",
      description: "",
      dueAtOffsetDays: undefined,
    })
  }

  const openTaskEdit = (task: TaskLike) => {
    setTaskMode("edit")
    setEditingTaskId(task.id)
    taskForm.reset({
      title: task.title,
      description: task.description || "",
      defaultAssignedToEmail: task.defaultAssignedToEmail || "",
      dueAtOffsetDays: task.dueAtOffsetDays ?? undefined,
    })
  }

  const handleTaskSave = async (values: TaskFormValues) => {
    const email = values.defaultAssignedToEmail.trim()
    const body = values.description.trim()
    const title = values.title.trim()

    try {
      if (taskMode === "create") {
        const nextOrder =
          (displayedTasks || []).reduce<number>(
            (max, t) => Math.max(max, t.orderIndex),
            0,
          ) + 1
        if (currentWorkflow) {
          await dispatch(
            createTask({
              workflowId: currentWorkflow.id,
              payload: {
                title,
                description: body,
                defaultAssignedToEmail: email,
                orderIndex: nextOrder,
                dueAtOffsetDays: values.dueAtOffsetDays ?? null,
              },
            }),
          ).unwrap()
        } else {
          setDraftTasks((prev) => [
            ...prev,
            {
              title,
              description: body,
              defaultAssignedToEmail: email,
              orderIndex: nextOrder,
              dueAtOffsetDays: values.dueAtOffsetDays ?? null,
              id: crypto.randomUUID
                ? crypto.randomUUID()
                : Date.now().toString(),
            },
          ])
        }
        toast.success("Task added")
        taskForm.reset({
          title: "",
          defaultAssignedToEmail: "",
          description: "",
          dueAtOffsetDays: undefined,
        })
      } else if (editingTaskId) {
        if (currentWorkflow) {
          await dispatch(
            updateTask({
              workflowId: currentWorkflow.id,
              taskId: editingTaskId,
              payload: {
                title,
                description: body,
                defaultAssignedToEmail: email,
                orderIndex:
                  displayedTasks.find((t) => t.id === editingTaskId)
                    ?.orderIndex ?? 1,
                dueAtOffsetDays: values.dueAtOffsetDays ?? null,
              },
            }),
          ).unwrap()
        } else {
          setDraftTasks((prev) =>
            prev.map((t) =>
              t.id === editingTaskId
                ? {
                    title,
                    description: body,
                    defaultAssignedToEmail: email,
                    orderIndex: t.orderIndex,
                    dueAtOffsetDays: values.dueAtOffsetDays ?? null,
                    id: t.id,
                  }
                : t,
            ),
          )
        }
        toast.success("Task updated")
      }
      setEditingTaskId(null)
      openTaskCreate()
    } catch (err) {
      toast.error((err as string) || "Failed to save task")
    }
  }

  const handleTaskDelete = async (taskId: string, taskTitle: string) => {
    setConfirmDialog({
      open: true,
      type: "delete-task",
      taskId,
      taskTitle,
    })
  }

  const handleConfirmDeleteTask = async () => {
    if (!confirmDialog.taskId) return

    setIsSubmitting(true)
    try {
      if (currentWorkflow) {
        await dispatch(
          deleteTask({
            workflowId: currentWorkflow.id,
            taskId: confirmDialog.taskId,
          }),
        ).unwrap()
      } else {
        setDraftTasks((prev) =>
          prev.filter((t) => t.id !== confirmDialog.taskId),
        )
      }
      toast.success("Task deleted")
      setConfirmDialog({ open: false, type: "unsaved" })
    } catch (err) {
      toast.error((err as string) || "Failed to delete task")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toUpsertPayload = (
    task: TaskLike,
    orderOverride?: number,
  ): UpsertWorkflowTaskDto => ({
    title: task.title,
    description: task.description ?? "",
    defaultAssignedToEmail: task.defaultAssignedToEmail ?? "",
    orderIndex: orderOverride ?? task.orderIndex,
    dueAtOffsetDays: task.dueAtOffsetDays ?? null,
  })

  const moveTask = async (taskId: string, direction: "up" | "down") => {
    const sorted = [...displayedTasks].sort(
      (a, b) => a.orderIndex - b.orderIndex,
    )
    const idx = sorted.findIndex((t) => t.id === taskId)
    if (idx === -1) return

    const swapWith = direction === "up" ? idx - 1 : idx + 1
    if (swapWith < 0 || swapWith >= sorted.length) return

    const current = sorted[idx]
    const target = sorted[swapWith]
    const tempOrder = (sorted[sorted.length - 1]?.orderIndex || 0) + 1

    // To avoid duplicate order collisions and keep orderIndex >= 1, use a temp order above current max
    try {
      if (currentWorkflow) {
        await dispatch(
          updateTask({
            workflowId: currentWorkflow.id,
            taskId: current.id,
            payload: toUpsertPayload(current, tempOrder),
          }),
        ).unwrap()

        await dispatch(
          updateTask({
            workflowId: currentWorkflow.id,
            taskId: target.id,
            payload: toUpsertPayload(target, current.orderIndex),
          }),
        ).unwrap()

        await dispatch(
          updateTask({
            workflowId: currentWorkflow.id,
            taskId: current.id,
            payload: toUpsertPayload(current, target.orderIndex),
          }),
        ).unwrap()
      } else {
        setDraftTasks((prev) => {
          const next = [...prev]
          const currIdx = next.findIndex((t) => t.id === current.id)
          const targIdx = next.findIndex((t) => t.id === target.id)
          if (currIdx === -1 || targIdx === -1) return prev

          // use temp order to avoid duplicates
          next[currIdx] = { ...next[currIdx], orderIndex: tempOrder }
          next[targIdx] = { ...next[targIdx], orderIndex: current.orderIndex }
          next[currIdx] = { ...next[currIdx], orderIndex: target.orderIndex }
          return next
        })
      }
      toast.success("Task order updated")
    } catch (err) {
      toast.error((err as string) || "Failed to reorder tasks")
    }
  }

  const sortedTasks = [...(displayedTasks || [])].sort(
    (a, b) => a.orderIndex - b.orderIndex,
  )

  return (
    <div className="p-6 space-y-6">
      <div className="bg-card rounded-lg border shadow-sm p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="w-fit pl-0 hover:text-primary text-muted-foreground transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold">
                {isEditing ? "Edit Workflow" : "Create New Workflow"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditing
                  ? "Modify workflow details and tasks"
                  : "Name your workflow and add the steps"}
              </p>
            </div>
          </div>
        </div>

        {/* Workflow Details */}
        <Form {...workflowForm}>
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Workflow Details</h2>

            <div className="space-y-4">
              <FormField
                control={workflowForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter workflow name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={workflowForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g., This workflow covers issuing a laptop, shirt, etc."
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Add context for what this workflow is used for.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>

        <div className="border-t pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">
              Tasks ({sortedTasks.length})
            </h2>
          </div>

          <Form {...taskForm}>
            <form
              onSubmit={taskForm.handleSubmit(handleTaskSave)}
              className="rounded-lg p-4 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={taskForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter task title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={taskForm.control}
                  name="defaultAssignedToEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assignee Email *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="person@company.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={taskForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (email body) *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="This will be sent to the assignee"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={taskForm.control}
                name="dueAtOffsetDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due in (days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g., 7 (leave blank for no due date)"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value
                          field.onChange(val === "" ? null : parseInt(val, 10))
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormDescription>
                      Number of days after workflow starts. Leave blank if no
                      deadline.
                    </FormDescription>
                  </FormItem>
                )}
              />

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="submit"
                  disabled={isLoading || !taskForm.formState.isValid}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {taskMode === "create" ? "Add Task" : "Update Task"}
                </Button>
                {taskMode === "edit" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openTaskCreate}
                    disabled={isLoading}
                  >
                    Cancel Edit
                  </Button>
                )}
              </div>
            </form>
          </Form>

          {sortedTasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No tasks yet. Add your first task to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedTasks.map((task, index) => (
                <div
                  key={task.id}
                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        #{task.orderIndex}
                      </span>
                      <h3 className="font-medium">{task.title}</h3>
                    </div>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {task.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Email: {task.defaultAssignedToEmail || "-"}</span>
                      {task.dueAtOffsetDays != null && (
                        <span>Due in: {task.dueAtOffsetDays} days</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveTask(task.id, "up")}
                      disabled={isLoading || index === 0}
                      title="Move up"
                    >
                      <MoveUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveTask(task.id, "down")}
                      disabled={isLoading || index === sortedTasks.length - 1}
                      title="Move down"
                    >
                      <MoveDown className="h-4 w-4" />
                    </Button>
                    <TooltipWrap
                      title="Edit Task"
                      description="Edit this task's title, description, assignee, or due date offset."
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTaskEdit(task)}
                        disabled={isLoading}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TooltipWrap>
                    <TooltipWrap
                      title="Delete Task"
                      description="Permanently deletes this task from the workflow template. This cannot be undone, and it also deletes this task's progress on any workflow instances currently running — including ones in progress."
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTaskDelete(task.id, task.title)}
                        disabled={isLoading}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipWrap>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Final actions */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={workflowForm.handleSubmit(handleSaveWorkflow)}
            disabled={isLoading || !workflowForm.formState.isValid}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isEditing ? "Update Workflow" : "Create Workflow"}
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <WorkflowConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDialog({ open: false, type: "unsaved" })
          }
        }}
        onConfirm={
          confirmDialog.type === "unsaved"
            ? handleConfirmUnsaved
            : handleConfirmDeleteTask
        }
        title={
          confirmDialog.type === "unsaved" ? "Unsaved Changes" : "Delete Task"
        }
        description={
          confirmDialog.type === "unsaved"
            ? "You have unsaved changes. Are you sure you want to leave? All changes will be lost."
            : `Are you sure you want to delete "${confirmDialog.taskTitle}"? This action cannot be undone.`
        }
        confirmText={confirmDialog.type === "unsaved" ? "Leave" : "Delete"}
        cancelText="Cancel"
        isSubmitting={isSubmitting}
        variant={confirmDialog.type === "unsaved" ? "warning" : "danger"}
      />
    </div>
  )
}
